import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Search } from 'lucide-react';
import { CountryFlag } from '../../utils/countryFlag';
import { HouseIcon, InfoIcon, LightningBoltIcon, SearchIcon } from '../icons';
import CountrySearchModal from '../modals/CountrySearchModal';
import { useCompactViewport } from '../../hooks/useCompactViewport';

const ENERGY_PROFILES_BUTTON_STYLE = {
  fontFamily: 'Inter, system-ui, sans-serif',
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '100%',
  letterSpacing: '0%',
  color: '#3B3B3B',
};

const CountryHeader = React.forwardRef(function CountryHeader(
  {
    country,
    countries,
    showHourlySimulator,
    onUnderstandingProfilesClick,
    onCountrySelect,
    onSimulatorToggle,
  },
  ref
) {
  const isCompact = useCompactViewport();
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const compactSubheading = showHourlySimulator
    ? 'Energy Simulation Dashboard'
    : 'Energy Analysis Dashboard';

  const pageTitle = showHourlySimulator
    ? `${country.name}'s Hourly Simulation`
    : `${country.name}'s Energy Analysis`;

  const filteredCountries = useMemo(
    () =>
      countries.filter(
        (c) =>
          c.name?.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
          c.iso_code?.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
          c.iso2_code?.toLowerCase().includes(countrySearchTerm.toLowerCase())
      ),
    [countries, countrySearchTerm]
  );

  const handleOpenCountrySearch = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCountryModal(true);
  }, []);

  const handleCountryPickerToggle = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isCompact) {
        setShowCountryModal(true);
      } else {
        setShowCountryDropdown((prev) => !prev);
      }
    },
    [isCompact]
  );

  const handleCloseCountryModal = useCallback(() => {
    setShowCountryModal(false);
  }, []);

  const handleSearchChange = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setCountrySearchTerm(e.target.value);
  }, []);

  const handleSearchClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleSearchKeyDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleDropdownPanelClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleCountryItemClick = useCallback(
    (e, selectedCountry) => {
      e.preventDefault();
      e.stopPropagation();
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
      onCountrySelect(selectedCountry);
    },
    [onCountrySelect]
  );

  const handleSimulatorToggleClick = useCallback(
    (e) => {
      e.stopPropagation();
      onSimulatorToggle();
    },
    [onSimulatorToggle]
  );

  useEffect(() => {
    if (isCompact) {
      setShowCountryDropdown(false);
      setCountrySearchTerm('');
    } else {
      setShowCountryModal(false);
    }
  }, [isCompact]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearchTerm('');
      }
    };

    if (showCountryDropdown && !isCompact) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCountryDropdown, isCompact]);

  return (
    <div ref={ref} className="px-4 sm:px-6 pt-3 pb-2 sm:pt-4 sm:pb-3">
      {/* Mobile & tablet (≤1024px) */}
      <div className="lg:hidden flex items-center gap-3">
        <button
          type="button"
          onClick={handleOpenCountrySearch}
          className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 rounded-lg py-2 pr-1 text-left transition-colors active:bg-gray-100/80"
          aria-label="Search and select country"
          aria-haspopup="dialog"
          aria-expanded={showCountryModal}
        >
          <SearchIcon size={29} className="shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-base font-bold uppercase tracking-wide text-gray-900">
              {country.name}
            </span>
            <span className="block truncate text-[14px] font-semibold leading-normal tracking-normal text-[#898989]">
              {compactSubheading}
            </span>
          </span>
        </button>

        {showHourlySimulator ? (
          <button
            type="button"
            onClick={handleSimulatorToggleClick}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-vervestacks-primary text-white shadow-sm transition-all hover:opacity-90 active:scale-95"
            title="Back to Dashboard"
            aria-label="Back to Dashboard"
          >
            <HouseIcon size={17} className="shrink-0" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSimulatorToggleClick}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-kanors text-white shadow-sm transition-all hover:opacity-90 active:scale-95"
            title="Launch Simulator"
            aria-label="Launch Simulator"
          >
            <LightningBoltIcon size={20} className="shrink-0" aria-hidden />
          </button>
        )}
      </div>

      {/* Desktop (≥1025px) — unchanged */}
      <div className="hidden lg:flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
          <Link
            to="/"
            className="text-vervestacks-primary hover:text-vervestacks-primary/80 transition-colors flex-shrink-0"
            aria-label="Back to home"
          >
            <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          </Link>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
            {pageTitle}
          </h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-end flex-wrap">
          {showHourlySimulator && (
            <button
              type="button"
              onClick={onUnderstandingProfilesClick}
              className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity"
              style={ENERGY_PROFILES_BUTTON_STYLE}
              title="Demand and resource profiles"
              aria-label="Demand and resource profiles"
            >
              <span>Demand and resource profiles</span>
              <InfoIcon size={16} className="shrink-0" />
            </button>
          )}

          <div className="relative" ref={dropdownRef}>
            <div
              role="button"
              tabIndex={0}
              onClick={handleCountryPickerToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleCountryPickerToggle(e);
                }
              }}
              className="bg-white border border-gray-200 rounded-md px-3 sm:px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
              aria-haspopup="listbox"
              aria-expanded={showCountryDropdown}
            >
              <div className="flex items-center space-x-2">
                <CountryFlag countryData={country} />
                <span className="text-gray-900 font-medium text-xs sm:text-sm">
                  {country.name}
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    showCountryDropdown ? 'rotate-180' : ''
                  }`}
                />
              </div>
            </div>

            {showCountryDropdown && (
              <div
                className="absolute top-full mt-2 right-0 w-72 sm:w-80 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 flex flex-col"
                style={{ zIndex: 9999 }}
                onClick={handleDropdownPanelClick}
              >
                <div className="p-3 border-b border-gray-200">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search countries..."
                      value={countrySearchTerm}
                      onChange={handleSearchChange}
                      onClick={handleSearchClick}
                      onKeyDown={handleSearchKeyDown}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-base lg:text-sm focus:ring-2 focus:ring-vervestacks-primary focus:border-transparent"
                      autoFocus
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                <div className="overflow-y-auto max-h-80">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((c) => (
                      <div
                        key={c.iso_code}
                        onClick={(e) => handleCountryItemClick(e, c)}
                        className={`flex items-center justify-between px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors ${
                          c.iso_code === country.iso_code ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <CountryFlag countryData={c} />
                          <span className="text-sm font-medium text-gray-900">{c.name}</span>
                        </div>
                        <span className="text-xs text-gray-500 font-mono">{c.iso_code}</span>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No countries found</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={showHourlySimulator}
            onClick={onSimulatorToggle}
            className={`${
              showHourlySimulator ? 'btn-primary' : 'btn-outline'
            } px-3 sm:px-5 py-2 rounded-md font-medium text-xs sm:text-sm transition-all duration-200 whitespace-nowrap focus:outline-none focus:ring-0 focus:ring-offset-0`}
            title={showHourlySimulator ? 'Back to Dashboard' : 'Launch Simulator'}
          >
            {showHourlySimulator ? 'Back to Dashboard' : 'Launch Simulator'}
          </button>
        </div>
      </div>

      <CountrySearchModal
        isOpen={showCountryModal && isCompact}
        onClose={handleCloseCountryModal}
        countries={countries}
        selectedCountry={country}
        onCountrySelect={onCountrySelect}
      />
    </div>
  );
});

export default React.memo(CountryHeader);
